/**
 * Gateway AI analytics tools — ports the editor's data-investigation
 * tool surface so the trends-page AI can actually look at historian
 * data instead of guessing. Same names, same parameter shapes, same
 * return shapes as the editor agent tools, so prompt instructions
 * carry over.
 *
 * Each tool returns `{ ok: true, data }` or `{ ok: false, error }` —
 * never throws across the registry boundary.
 *
 * Tool families:
 *   • Tag discovery:    search_tags, list_tags_by_prefix,
 *                       get_tag_metadata, get_tag_value
 *   • Historian queries: query_historian (aggregate),
 *                        query_historian_last, query_historian_raw,
 *                        value_at
 *   • Server-side analytics: analyze_timeseries, find_anomalies,
 *                            describe_distribution,
 *                            detect_change_points, rate_of_change
 *   • Client-side analytics (pure JS over passed-in points):
 *       correlate_tags, cross_correlate_lag, compare_ranges,
 *       compute_uptime, find_active_tags
 */

import * as historian from "../analytics/historianBackend";
import * as tagSearch from "../analytics/tagSearch";
import { resolveTimeRange, resolveTimeShortcut } from "../analytics/timeParse";
import {
  mean,
  median,
  standardDeviation,
  min as statsMin,
  max as statsMax,
  linearRegression,
  linearRegressionLine,
  rSquared,
  zScore,
  quantile,
  sampleCorrelation,
  variance,
} from "../analytics/stats";

// ── shared helpers ─────────────────────────────────────────────────

function badRange(err) {
  return {
    ok: false,
    error: {
      code: "BAD_TIME_RANGE",
      message: err?.message || String(err),
      hint: "Use RFC3339 ('2026-04-23T14:00:00Z') or a shortcut like 'now-1h'.",
    },
  };
}

function historianFailure(err) {
  return {
    ok: false,
    error: {
      code: "HISTORIAN_QUERY_FAILED",
      message: err?.message || String(err),
      hint: "Check the tag path with search_tags and confirm the range is valid.",
    },
  };
}

function extractValue(p, valueField) {
  if (!p || typeof p !== "object") return null;
  let v;
  if (valueField && p[valueField] != null) v = p[valueField];
  else if (typeof p.v === "number") v = p.v;
  else if (typeof p.avg === "number") v = p.avg;
  else if (typeof p.last === "number") v = p.last;
  else if (typeof p.max === "number") v = p.max;
  else if (typeof p.min === "number") v = p.min;
  else return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// ── Tag discovery ──────────────────────────────────────────────────

export const search_tags = {
  name: "search_tags",
  description:
    "Search for available tags by name or path. Results are ranked: exact_name > exact_path > name_prefix > segment_exact > segment_prefix > name_substring > path_substring. " +
    "When the user types a short literal (e.g. 'kw', 'pump') prefer the first exact_name/exact_path result, NOT a fuzzy substring match.",
  parameters: {
    type: "object",
    properties: {
      query: { type: "string", description: "Search term to match against tag names and paths." },
      type: {
        type: "string",
        enum: ["number", "boolean", "string", "json"],
        description: "Optional data-type filter.",
      },
      limit: { type: "number", description: "Max results to return (default 25, max 50)." },
    },
    required: ["query"],
    additionalProperties: false,
  },
  async execute({ query, type, limit }) {
    try {
      const cap = Math.min(typeof limit === "number" ? limit : 25, 50);
      const raw = await tagSearch.searchTags(query, { limit: cap, type: type || "" });
      const results = raw.map((r) => ({
        fullPath: r.fullPath,
        name: r.name,
        type: r.type || null,
        folder_path: r.folderPath || "",
        matchType: r.matchType,
      }));
      return { ok: true, data: { results } };
    } catch (err) {
      return { ok: false, error: { code: "TAG_SEARCH_FAILED", message: String(err?.message || err) } };
    }
  },
};

export const list_tags_by_prefix = {
  name: "list_tags_by_prefix",
  description:
    "List every tag under a folder path or a full-path prefix, e.g. 'default/solis' or 'default/Process/Pumps' " +
    "— matches that folder itself plus everything nested under it (segment-boundary match: 'default/solis' " +
    "matches 'default/solis/Battery_Voltage' but not 'default/solis-backup/x'; trailing slash and case don't matter). " +
    "Unranked — returns the full set up to `limit`. Use for fan-out discovery ('every tag under default/pump') " +
    "and to resolve a folder the user names into the full tag paths a bulk mutation needs. " +
    "`depth`=1 means direct children only.",
  parameters: {
    type: "object",
    properties: {
      prefix: { type: "string", description: "Folder path or tag-path prefix (e.g. 'default/Process/Pumps')." },
      depth: { type: "number", description: "Max path segments beyond the prefix. 1 = direct children, omitted = all descendants." },
      type: {
        type: "string",
        enum: ["number", "boolean", "string", "json"],
        description: "Optional data-type filter.",
      },
      limit: { type: "number", description: "Max tags returned. Default 200, max 1000." },
    },
    required: ["prefix"],
    additionalProperties: false,
  },
  async execute({ prefix, depth, type, limit }) {
    try {
      const cap = Math.min(Math.max(Number(limit) || 200, 1), 1000);
      const d = Number.isFinite(depth) && depth > 0 ? Math.floor(depth) : Infinity;
      const raw = await tagSearch.listTagsByPrefix(prefix, { limit: cap, depth: d, type: type || "" });
      return {
        ok: true,
        data: {
          prefix,
          depth: d === Infinity ? null : d,
          results: raw.map((r) => ({
            fullPath: r.fullPath,
            name: r.name,
            type: r.type || null,
            folder_path: r.folderPath || "",
          })),
          ...(raw.length >= cap ? { truncated: true } : {}),
        },
      };
    } catch (err) {
      return { ok: false, error: { code: "TAG_LIST_FAILED", message: String(err?.message || err) } };
    }
  },
};

export const get_tag_metadata = {
  name: "get_tag_metadata",
  description:
    "Get full metadata for a tag (type, description, folder, source, writable, units if configured). " +
    "Call this BEFORE reasoning about historian values when units or range are ambiguous.",
  parameters: {
    type: "object",
    properties: { tag_path: { type: "string", description: "Full tag path." } },
    required: ["tag_path"],
    additionalProperties: false,
  },
  async execute({ tag_path }) {
    try {
      const result = await historian.tagMetadata(tag_path);
      if (!result) {
        return {
          ok: false,
          error: {
            code: "TAG_NOT_FOUND",
            message: `Tag "${tag_path}" not found.`,
            hint: "Use search_tags to discover valid paths.",
          },
        };
      }
      // Strip the numeric tagId from the surface — AI tools key by path.
      const { tagId: _id, ...rest } = result;
      return { ok: true, data: rest };
    } catch (err) {
      return historianFailure(err);
    }
  },
};

export const get_tag_value = {
  name: "get_tag_value",
  description:
    "Get the most recent value for a single tag. Returns {value, quality, timestamp}. " +
    "For history use query_historian / query_historian_raw / query_historian_last.",
  parameters: {
    type: "object",
    properties: { tag_path: { type: "string", description: "Full tag path." } },
    required: ["tag_path"],
    additionalProperties: false,
  },
  async execute({ tag_path }) {
    try {
      const result = await historian.currentValue(tag_path);
      if (!result) {
        return {
          ok: false,
          error: {
            code: "TAG_VALUE_UNAVAILABLE",
            message: `No current value for "${tag_path}".`,
            hint: "The tag may have never been written. Try query_historian_last or confirm the path with search_tags.",
          },
        };
      }
      return {
        ok: true,
        data: {
          tagPath: tag_path,
          value: result.v,
          quality: result.quality ?? null,
          quality_label: result.quality_label ?? null,
          timestamp: result.t ?? null,
        },
      };
    } catch (err) {
      return historianFailure(err);
    }
  },
};

// ── Historian queries ──────────────────────────────────────────────

export const query_historian = {
  name: "query_historian",
  description:
    "Fetch aggregated time-series data for a tag over a time range. " +
    "Returns {points: [{t, min, max, avg, last, count}]} — one bucket per interval. " +
    "Use for overview / trend questions ('pump.speed over the last hour in 1m buckets'). " +
    "Time args accept RFC3339 or 'now-Xm|h|d' shortcuts.",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      start: { type: "string", description: "Range start (RFC3339 or 'now-Xm|h|d')." },
      end: { type: "string", description: "Range end. Defaults to 'now'." },
      interval: { type: "string", description: "Bucket width: '10s' | '30s' | '1m' | '5m' | '15m' | '1h'. Default '1m'." },
      max_points: { type: "number", description: "Hard cap on returned buckets. Default 500, max 5000." },
    },
    required: ["tag_path", "start"],
    additionalProperties: false,
  },
  async execute({ tag_path, start, end, interval, max_points }) {
    let range;
    try { range = resolveTimeRange(start, end ?? "now"); } catch (err) { return badRange(err); }
    const cap = Math.min(Math.max(1, Number(max_points) || 500), 5000);
    // If the caller passed an interval, honor it verbatim (they likely
    // care about the exact resolution). Otherwise auto-pick the
    // smallest interval that keeps the bucket count under the cap —
    // mirrors the trends-page logic so a 24h ask doesn't error out
    // just because the default would emit 1440 1-minute buckets.
    const intervalLabel = typeof interval === "string" && interval.trim()
      ? interval.trim()
      : pickIntervalUnderCap(Date.parse(range.start), Date.parse(range.end), cap);
    let result;
    try {
      result = await historian.queryAggregated(tag_path, {
        start: range.start, end: range.end, interval: intervalLabel,
      });
    } catch (err) { return historianFailure(err); }
    const points = result.points || [];
    if (points.length > cap) {
      return {
        ok: false,
        error: {
          code: "RESULT_TOO_LARGE",
          message: `Query returned ${points.length} buckets; cap is ${cap}.`,
          hint: "Widen the interval (e.g. '5m' instead of '1m'), narrow the range, or raise max_points (hard max 5000).",
        },
      };
    }
    return {
      ok: true,
      data: {
        tagPath: tag_path,
        range: { start: range.start, end: range.end, interval: intervalLabel },
        points,
      },
    };
  },
};

// pickIntervalUnderCap — auto-pick the smallest historian interval
// that won't exceed `cap` buckets for the given window. Same 1-2-5
// ladder the editor Trends Workbench uses; duplicated here so this
// module stays self-contained.
const INTERVAL_LADDER = [
  ["1s", 1], ["2s", 2], ["5s", 5], ["10s", 10], ["20s", 20], ["30s", 30],
  ["1m", 60], ["2m", 120], ["5m", 300], ["10m", 600], ["20m", 1200], ["30m", 1800],
  ["1h", 3600], ["2h", 7200], ["4h", 14400], ["6h", 21600], ["12h", 43200],
  ["24h", 86400], ["48h", 172800], ["168h", 604800],
];
function pickIntervalUnderCap(startMs, endMs, cap) {
  const rangeSec = Math.max(1, Math.round((endMs - startMs) / 1000));
  for (const [label, sec] of INTERVAL_LADDER) {
    if (rangeSec / sec <= cap) return label;
  }
  return "168h";
}

export const query_historian_last = {
  name: "query_historian_last",
  description:
    "Fetch the last N raw data points for a tag, newest first. " +
    "Returns {points: [{t, v, quality}]}. " +
    "Use for quick 'latest readings' questions.",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      n: { type: "number", description: "Number of points to return. Default 10, max 500." },
    },
    required: ["tag_path"],
    additionalProperties: false,
  },
  async execute({ tag_path, n }) {
    const count = Math.min(Math.max(1, Number(n) || 10), 500);
    try {
      const result = await historian.queryLatest(tag_path, count);
      return { ok: true, data: { tagPath: tag_path, n: count, points: result.points || [] } };
    } catch (err) { return historianFailure(err); }
  },
};

export const query_historian_raw = {
  name: "query_historian_raw",
  description:
    "Fetch RAW (unaggregated) time-series points for a tag. " +
    "Returns {points: [{t, v, quality}]}. " +
    "Use for precise values near a specific event. For broad trends use query_historian.",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      start: { type: "string", description: "Range start (RFC3339 or 'now-Xm|h|d')." },
      end: { type: "string", description: "Range end. Defaults to 'now'." },
      order: { type: "string", enum: ["asc", "desc"], description: "Sort order. Default 'asc'." },
      max_points: { type: "number", description: "Hard cap. Default 500, max 5000." },
    },
    required: ["tag_path", "start"],
    additionalProperties: false,
  },
  async execute({ tag_path, start, end, order, max_points }) {
    let range;
    try { range = resolveTimeRange(start, end ?? "now"); } catch (err) { return badRange(err); }
    const cap = Math.min(Math.max(1, Number(max_points) || 500), 5000);
    let result;
    try {
      result = await historian.queryRaw(tag_path, {
        start: range.start, end: range.end,
        limit: 10000,
        order: order === "desc" ? "desc" : "asc",
      });
    } catch (err) { return historianFailure(err); }
    const points = result.points || [];
    if (points.length > cap) {
      return {
        ok: false,
        error: {
          code: "RESULT_TOO_LARGE",
          message: `Query returned ${points.length} raw points; cap is ${cap}.`,
          hint: "Narrow the range or raise max_points (max 5000).",
        },
      };
    }
    return {
      ok: true,
      data: { tagPath: tag_path, range: { start: range.start, end: range.end }, points },
    };
  },
};

export const value_at = {
  name: "value_at",
  description:
    "Get the value a tag was holding at a specific moment (zero-order hold). " +
    "Returns {t, v, quality, age_seconds}. Use for 'what was X at 14:02:17', " +
    "'what was the reading when the alarm fired'. Looks back up to max_lookback_days (default 7).",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      timestamp: { type: "string", description: "Moment to query (RFC3339 or 'now'). Defaults to 'now'." },
      max_lookback_days: { type: "number", description: "How far back to search. Default 7, max 30." },
    },
    required: ["tag_path"],
    additionalProperties: false,
  },
  async execute({ tag_path, timestamp, max_lookback_days }) {
    let targetIso;
    try { targetIso = resolveTimeShortcut(timestamp || "now"); } catch (err) {
      return { ok: false, error: { code: "BAD_TIMESTAMP", message: err.message } };
    }
    const days = Math.min(Math.max(1, Number(max_lookback_days) || 7), 30);
    const lookbackMs = days * 24 * 60 * 60 * 1000;
    let result;
    try { result = await historian.valueAt(tag_path, targetIso, { lookbackMs }); }
    catch (err) { return historianFailure(err); }
    if (!result) {
      return {
        ok: false,
        error: {
          code: "VALUE_NOT_FOUND",
          message: `No sample at or before ${targetIso} within the last ${days} days.`,
          hint: "The tag may have stopped writing. Try query_historian_last for the most recent value globally, or widen max_lookback_days.",
        },
      };
    }
    return {
      ok: true,
      data: {
        tagPath: tag_path,
        target: targetIso,
        t: result.t ?? null,
        v: result.v,
        quality: result.quality ?? null,
        quality_label: result.quality_label ?? null,
        age_seconds: Number.isFinite(result.ageMs) ? Math.round(result.ageMs / 1000) : null,
      },
    };
  },
};

// ── Server-side analytics push-downs ──────────────────────────────

export const analyze_timeseries = {
  name: "analyze_timeseries",
  description:
    "Descriptive stats + linear trend over a tag and time range. Server-side push-down, single round trip. " +
    "Returns {count, min, max, mean, median, stddev, trend: {slope, intercept, r2}}. " +
    "Use for 'summarize X over the last 24h'.",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      start: { type: "string", description: "Range start (RFC3339 or 'now-Xm|h|d')." },
      end: { type: "string", description: "Range end. Default 'now'." },
    },
    required: ["tag_path", "start"],
    additionalProperties: false,
  },
  async execute({ tag_path, start, end }) {
    let range;
    try { range = resolveTimeRange(start, end ?? "now"); } catch (err) { return badRange(err); }
    try {
      const out = await historian.analyzeTimeseries(tag_path, { start: range.start, end: range.end });
      return {
        ok: true,
        data: { tagPath: tag_path, range: { start: range.start, end: range.end }, result: out.result },
      };
    } catch (err) { return historianFailure(err); }
  },
};

export const find_anomalies = {
  name: "find_anomalies",
  description:
    "Flag anomalous points in a tag's time-series. Server filters and returns only flagged rows. " +
    "method='auto' (default) finds the natural elbow in the score distribution — preferred for unfamiliar tags. " +
    "'zscore' flags |z|>threshold (default 3). 'mad' is Hampel-style, robust to autocorrelation (threshold 3.5). " +
    "'stuck' flags frozen runs (threshold = min consecutive samples). 'cusum' for sustained drift. " +
    "Use cluster_gap_seconds to collapse adjacent flagged samples into events (default 60).",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      start: { type: "string", description: "Range start (RFC3339 or 'now-Xm|h|d')." },
      end: { type: "string", description: "Range end. Default 'now'." },
      method: {
        type: "string",
        enum: ["auto", "zscore", "iqr", "mad", "percentile", "stuck", "cusum"],
        description: "Detection algorithm. Default 'auto'.",
      },
      threshold: { type: "number", description: "Per-method cutoff." },
      window: { type: "number", description: "Rolling window size in samples (zscore, mad, percentile, auto)." },
      percentile: { type: "number", description: "For method='percentile'. For 'cusum' the allowance k in σ." },
      cluster_gap_seconds: { type: "number", description: "Collapse consecutive flagged samples within this many seconds. Default 60." },
    },
    required: ["tag_path", "start"],
    additionalProperties: false,
  },
  async execute({ tag_path, start, end, method, threshold, window, percentile, cluster_gap_seconds }) {
    let range;
    try { range = resolveTimeRange(start, end ?? "now"); } catch (err) { return badRange(err); }
    try {
      const out = await historian.findAnomalies(tag_path, {
        start: range.start, end: range.end,
        method: method || "auto",
        threshold, window, percentile,
        cluster_gap_seconds: cluster_gap_seconds ?? 60,
      });
      return {
        ok: true,
        data: {
          tagPath: tag_path,
          range: { start: range.start, end: range.end },
          method: method || "auto",
          rows: out.rows,
        },
      };
    } catch (err) { return historianFailure(err); }
  },
};

export const describe_distribution = {
  name: "describe_distribution",
  description:
    "Compute distribution shape for a tag over a range: histogram bins + percentiles + descriptive stats. " +
    "Use for 'what does the distribution of X look like', 'is this bimodal', 'where are the typical operating points'.",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      start: { type: "string", description: "Range start (RFC3339 or 'now-Xm|h|d')." },
      end: { type: "string", description: "Range end. Default 'now'." },
      bins: { type: "number", description: "Number of histogram bins. Default 20." },
      percentiles: {
        type: "array",
        items: { type: "number" },
        description: "Percentiles to compute (0..100). Default [5, 25, 50, 75, 95].",
      },
      approximate: { type: "boolean", description: "Use approximate quantiles (TDigest) for very large ranges." },
    },
    required: ["tag_path", "start"],
    additionalProperties: false,
  },
  async execute({ tag_path, start, end, bins, percentiles, approximate }) {
    let range;
    try { range = resolveTimeRange(start, end ?? "now"); } catch (err) { return badRange(err); }
    try {
      const out = await historian.distribution(tag_path, {
        start: range.start, end: range.end, bins, percentiles, approximate,
      });
      return {
        ok: true,
        data: { tagPath: tag_path, range: { start: range.start, end: range.end }, result: out.result },
      };
    } catch (err) { return historianFailure(err); }
  },
};

export const detect_change_points = {
  name: "detect_change_points",
  description:
    "Find timestamps where a tag's mean shifts significantly. Server-side change-point detection. " +
    "Returns the timestamps + before/after means. Use for 'when did X change behavior', 'when did the regime shift'.",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      start: { type: "string", description: "Range start (RFC3339 or 'now-Xm|h|d')." },
      end: { type: "string", description: "Range end. Default 'now'." },
      threshold: { type: "number", description: "Minimum |before - after| / pooled stddev to flag. Default 3." },
      window: { type: "number", description: "Window size in samples for the comparison. Default 60." },
    },
    required: ["tag_path", "start"],
    additionalProperties: false,
  },
  async execute({ tag_path, start, end, threshold, window }) {
    let range;
    try { range = resolveTimeRange(start, end ?? "now"); } catch (err) { return badRange(err); }
    try {
      const out = await historian.changePoints(tag_path, {
        start: range.start, end: range.end, threshold, window,
      });
      return {
        ok: true,
        data: {
          tagPath: tag_path,
          range: { start: range.start, end: range.end },
          rows: out.rows,
        },
      };
    } catch (err) { return historianFailure(err); }
  },
};

export const rate_of_change = {
  name: "rate_of_change",
  description:
    "Compute first-derivative statistics (slope between adjacent points). " +
    "Returns {max_rate, max_rate_at, min_rate, min_rate_at, mean_rate, stddev_rate}. " +
    "Use for 'how fast did X drop', 'when was the ramp sharpest', 'what was the peak rate'. " +
    "For overall trend direction use analyze_timeseries.",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      start: { type: "string", description: "Range start (RFC3339 or 'now-Xm|h|d')." },
      end: { type: "string", description: "Range end. Default 'now'." },
      unit: { type: "string", description: "Optional rate unit ('1s', '1m', '1h'). Default '1s'." },
    },
    required: ["tag_path", "start"],
    additionalProperties: false,
  },
  async execute({ tag_path, start, end, unit }) {
    let range;
    try { range = resolveTimeRange(start, end ?? "now"); } catch (err) { return badRange(err); }
    try {
      const out = await historian.rateOfChange(tag_path, { start: range.start, end: range.end, unit });
      const r = out.result || {};
      return {
        ok: true,
        data: {
          tagPath: tag_path,
          range: { start: range.start, end: range.end },
          slope_unit: unit ? `per_${unit}` : "per_second",
          n_intervals: r.count || 0,
          max_rate: r.max_rate ?? null,
          max_rate_at: r.max_rate_at ?? null,
          min_rate: r.min_rate ?? null,
          min_rate_at: r.min_rate_at ?? null,
          mean_rate: r.mean_rate ?? null,
          stddev_rate: r.stddev_rate ?? null,
          abs_mean_rate: r.abs_mean_rate ?? null,
        },
      };
    } catch (err) { return historianFailure(err); }
  },
};

// ── Client-side analytics (over points already in scope) ──────────

function withTs(points) {
  return (Array.isArray(points) ? points : [])
    .map((p) => ({ v: extractValue(p), t: p?.t ? new Date(p.t).getTime() : null }))
    .filter((p) => p.v != null && Number.isFinite(p.t))
    .sort((a, b) => a.t - b.t);
}

function alignOnTimestamp(a, b, toleranceMs) {
  const aTs = withTs(a);
  const bTs = withTs(b);
  if (!aTs.length || !bTs.length) return { xs: [], ys: [], pairs: [] };
  const xs = [];
  const ys = [];
  const pairs = [];
  let bIdx = 0;
  for (const pa of aTs) {
    while (bIdx + 1 < bTs.length && bTs[bIdx + 1].t <= pa.t) bIdx += 1;
    const candidates = [bTs[bIdx]];
    if (bIdx + 1 < bTs.length) candidates.push(bTs[bIdx + 1]);
    let best = null;
    let bestDelta = Infinity;
    for (const c of candidates) {
      const d = Math.abs(c.t - pa.t);
      if (d < bestDelta) { best = c; bestDelta = d; }
    }
    if (best && bestDelta <= toleranceMs) {
      xs.push(pa.v);
      ys.push(best.v);
      pairs.push({ t: pa.t, a: pa.v, b: best.v });
    }
  }
  return { xs, ys, pairs };
}

function hasVariance(arr) {
  if (arr.length < 2) return false;
  const first = arr[0];
  for (let i = 1; i < arr.length; i += 1) if (arr[i] !== first) return true;
  return false;
}

export const correlate_tags = {
  name: "correlate_tags",
  description:
    "Pearson correlation (-1..+1) between two tag time-series the model already fetched. " +
    "Aligns by timestamp within tolerance_ms (default 60000). " +
    "Use for 'did X and Y move together', 'are they related'.",
  parameters: {
    type: "object",
    properties: {
      a_points: { type: "array", description: "Points from the first series." },
      b_points: { type: "array", description: "Points from the second series." },
      tolerance_ms: { type: "number", description: "Max time offset for a match. Default 60000." },
    },
    required: ["a_points", "b_points"],
    additionalProperties: false,
  },
  async execute({ a_points, b_points, tolerance_ms }) {
    const tol = Number.isFinite(tolerance_ms) && tolerance_ms > 0 ? tolerance_ms : 60_000;
    const { xs, ys } = alignOnTimestamp(a_points, b_points, tol);
    if (xs.length < 2) {
      return {
        ok: false,
        error: {
          code: "INSUFFICIENT_OVERLAP",
          message: `Only ${xs.length} aligned point(s) — need at least 2 for correlation.`,
          hint: "Widen tolerance_ms, or query both tags over the same range with the same interval.",
        },
      };
    }
    if (!hasVariance(xs) || !hasVariance(ys)) {
      return {
        ok: false,
        error: {
          code: "ZERO_VARIANCE",
          message: "At least one series is constant over the overlap window — correlation is undefined.",
          hint: "Widen the range until both tags actually change.",
        },
      };
    }
    let pearson = sampleCorrelation(xs, ys);
    if (!Number.isFinite(pearson)) pearson = null;
    return { ok: true, data: { pearson, overlap_count: xs.length, tolerance_ms: tol } };
  },
};

export const cross_correlate_lag = {
  name: "cross_correlate_lag",
  description:
    "Find the time-lag at which two tag series are most correlated. Returns {best_lag_ms, best_pearson, samples}. " +
    "Positive lag = b lags a. Use for 'does X lead Y', 'what's the response time'.",
  parameters: {
    type: "object",
    properties: {
      a_points: { type: "array", description: "Points from the leading (or candidate-leading) series." },
      b_points: { type: "array", description: "Points from the responding series." },
      max_lag_ms: { type: "number", description: "Maximum lag to test, in ms. Default 5 minutes (300000)." },
      step_ms: { type: "number", description: "Lag step size in ms. Default 5000." },
    },
    required: ["a_points", "b_points"],
    additionalProperties: false,
  },
  async execute({ a_points, b_points, max_lag_ms, step_ms }) {
    const maxLag = Number.isFinite(max_lag_ms) && max_lag_ms > 0 ? max_lag_ms : 300_000;
    const step = Number.isFinite(step_ms) && step_ms > 0 ? step_ms : 5000;
    const aTs = withTs(a_points);
    const bTs = withTs(b_points);
    if (aTs.length < 2 || bTs.length < 2) {
      return { ok: false, error: { code: "INSUFFICIENT_DATA", message: "Need at least 2 points in each series." } };
    }
    let bestLag = 0;
    let bestR = -2; // valid Pearson is in [-1, 1]
    let bestN = 0;
    const samples = [];
    // Resample B at shifted timestamps from A; tolerance is the step.
    for (let lag = -maxLag; lag <= maxLag; lag += step) {
      const shifted = bTs.map((p) => ({ v: p.v, t: p.t - lag }));
      const xs = [];
      const ys = [];
      let bIdx = 0;
      for (const pa of aTs) {
        while (bIdx + 1 < shifted.length && shifted[bIdx + 1].t <= pa.t) bIdx += 1;
        const candidates = [shifted[bIdx]];
        if (bIdx + 1 < shifted.length) candidates.push(shifted[bIdx + 1]);
        let best = null;
        let bestDelta = Infinity;
        for (const c of candidates) {
          const d = Math.abs(c.t - pa.t);
          if (d < bestDelta) { best = c; bestDelta = d; }
        }
        if (best && bestDelta <= step) {
          xs.push(pa.v);
          ys.push(best.v);
        }
      }
      if (xs.length < 3 || !hasVariance(xs) || !hasVariance(ys)) continue;
      const r = sampleCorrelation(xs, ys);
      if (!Number.isFinite(r)) continue;
      samples.push({ lag_ms: lag, pearson: r, n: xs.length });
      if (r > bestR) { bestR = r; bestLag = lag; bestN = xs.length; }
    }
    if (bestR === -2) {
      return {
        ok: false,
        error: {
          code: "NO_VALID_LAG",
          message: "Couldn't compute correlation at any lag — try wider ranges or denser sampling.",
        },
      };
    }
    return {
      ok: true,
      data: {
        best_lag_ms: bestLag,
        best_pearson: bestR,
        sample_n: bestN,
        step_ms: step,
        samples: samples.slice(0, 200),
      },
    };
  },
};

export const compare_ranges = {
  name: "compare_ranges",
  description:
    "Compare descriptive stats between two time-ranges for the same tag. " +
    "Returns {before, after, delta_mean, percent_change}. " +
    "Use for 'is X higher this week than last week', 'before vs after a change'.",
  parameters: {
    type: "object",
    properties: {
      tag_path: { type: "string", description: "Full tag path." },
      a_start: { type: "string", description: "Range A start (RFC3339 or 'now-Xm|h|d')." },
      a_end: { type: "string", description: "Range A end." },
      b_start: { type: "string", description: "Range B start." },
      b_end: { type: "string", description: "Range B end." },
    },
    required: ["tag_path", "a_start", "a_end", "b_start", "b_end"],
    additionalProperties: false,
  },
  async execute({ tag_path, a_start, a_end, b_start, b_end }) {
    let rangeA;
    let rangeB;
    try {
      rangeA = resolveTimeRange(a_start, a_end);
      rangeB = resolveTimeRange(b_start, b_end);
    } catch (err) { return badRange(err); }
    let aResult;
    let bResult;
    try {
      [aResult, bResult] = await Promise.all([
        historian.analyzeTimeseries(tag_path, { start: rangeA.start, end: rangeA.end }),
        historian.analyzeTimeseries(tag_path, { start: rangeB.start, end: rangeB.end }),
      ]);
    } catch (err) { return historianFailure(err); }
    const a = aResult.result || {};
    const b = bResult.result || {};
    const am = Number.isFinite(a.mean) ? a.mean : null;
    const bm = Number.isFinite(b.mean) ? b.mean : null;
    const deltaMean = am != null && bm != null ? bm - am : null;
    const pctChange = am != null && am !== 0 && bm != null ? (deltaMean / am) * 100 : null;
    return {
      ok: true,
      data: {
        tagPath: tag_path,
        a: { range: rangeA, ...a },
        b: { range: rangeB, ...b },
        delta_mean: deltaMean,
        percent_change: pctChange,
      },
    };
  },
};

export const compute_uptime = {
  name: "compute_uptime",
  description:
    "Compute the fraction of time a tag was in a given state. " +
    "Pass the points from a query_historian_raw call and a predicate; tool computes total time the predicate held. " +
    "predicate=='running' counts samples > 0 (good default for run-status PVs). " +
    "predicate=='nonzero' counts samples != 0. predicate=='above'/'below' uses `threshold`.",
  parameters: {
    type: "object",
    properties: {
      points: { type: "array", description: "Points from query_historian_raw (must have t + v)." },
      predicate: {
        type: "string",
        enum: ["running", "nonzero", "above", "below"],
        description: "Which state counts as 'up'.",
      },
      threshold: { type: "number", description: "Threshold for 'above'/'below' predicates." },
    },
    required: ["points", "predicate"],
    additionalProperties: false,
  },
  async execute({ points, predicate, threshold }) {
    const sorted = withTs(points);
    if (sorted.length < 2) {
      return { ok: false, error: { code: "INSUFFICIENT_DATA", message: "Need at least 2 points to compute uptime." } };
    }
    const matches = (v) => {
      if (predicate === "running") return v > 0;
      if (predicate === "nonzero") return v !== 0;
      if (predicate === "above") return Number.isFinite(threshold) ? v > threshold : false;
      if (predicate === "below") return Number.isFinite(threshold) ? v < threshold : false;
      return false;
    };
    // Zero-order hold: each sample holds until the next.
    let upMs = 0;
    let totalMs = 0;
    for (let i = 1; i < sorted.length; i += 1) {
      const dt = sorted[i].t - sorted[i - 1].t;
      if (dt <= 0) continue;
      totalMs += dt;
      if (matches(sorted[i - 1].v)) upMs += dt;
    }
    if (totalMs <= 0) return { ok: false, error: { code: "NO_DURATION", message: "Points have no time extent." } };
    return {
      ok: true,
      data: {
        predicate,
        threshold: threshold ?? null,
        total_seconds: totalMs / 1000,
        up_seconds: upMs / 1000,
        uptime_fraction: upMs / totalMs,
      },
    };
  },
};

export const find_active_tags = {
  name: "find_active_tags",
  description:
    "Identify the tags with the most activity over a time range. " +
    "Pass a list of tag_paths; tool fetches a tiny aggregate query per tag and ranks by stddev (default) or count. " +
    "Use for 'which sensors have been moving most this week', 'find the noisiest tags'.",
  parameters: {
    type: "object",
    properties: {
      tag_paths: { type: "array", items: { type: "string" }, description: "List of tag paths to compare." },
      start: { type: "string", description: "Range start (RFC3339 or 'now-Xm|h|d')." },
      end: { type: "string", description: "Range end. Default 'now'." },
      rank_by: {
        type: "string",
        enum: ["stddev", "range", "count"],
        description: "Activity metric. Default 'stddev'.",
      },
      limit: { type: "number", description: "How many top tags to return. Default 10." },
    },
    required: ["tag_paths", "start"],
    additionalProperties: false,
  },
  async execute({ tag_paths, start, end, rank_by, limit }) {
    let range;
    try { range = resolveTimeRange(start, end ?? "now"); } catch (err) { return badRange(err); }
    const metric = rank_by || "stddev";
    const cap = Math.min(Math.max(Number(limit) || 10, 1), 100);
    const paths = Array.isArray(tag_paths) ? tag_paths : [];
    if (paths.length === 0) return { ok: false, error: { code: "NO_TAGS", message: "Pass at least one tag_path." } };
    const results = await Promise.all(paths.map(async (path) => {
      try {
        const out = await historian.analyzeTimeseries(path, { start: range.start, end: range.end });
        const r = out.result || {};
        const score =
          metric === "count" ? (r.count || 0) :
          metric === "range" ? ((r.max ?? 0) - (r.min ?? 0)) :
          (r.stddev ?? 0);
        return { tagPath: path, score, stats: r };
      } catch (err) {
        return { tagPath: path, score: null, error: String(err?.message || err) };
      }
    }));
    const ranked = results
      .filter((r) => Number.isFinite(r.score))
      .sort((a, b) => b.score - a.score)
      .slice(0, cap);
    const failed = results.filter((r) => !Number.isFinite(r.score)).map((r) => ({ tagPath: r.tagPath, error: r.error }));
    return {
      ok: true,
      data: {
        range: { start: range.start, end: range.end },
        rank_by: metric,
        results: ranked,
        ...(failed.length ? { failed } : {}),
      },
    };
  },
};

// ── Local-mode analytics (when AI already has points) ─────────────

// analyze_timeseries (LOCAL): runs over passed-in points instead of
// hitting the server. Useful when the AI already fetched points and
// just wants to summarize them without another round trip. Editor
// supports both modes — we surface it under a different name so the
// `tag_path` path stays the primary interface for the AI.
export const analyze_points = {
  name: "analyze_points",
  description:
    "Local descriptive stats + linear trend over a points array already in scope (from query_historian*). " +
    "Returns {count, min, max, mean, median, stddev, trend: {slope, intercept, r2}, outlier_count}. " +
    "For new analysis prefer analyze_timeseries (tag_path mode) — this is for points you already have.",
  parameters: {
    type: "object",
    properties: {
      points: { type: "array", description: "Points with {t, v|avg|last|...} shape." },
      value_field: { type: "string", description: "Override which field to read. Default v→avg→last." },
      outlier_threshold: { type: "number", description: "|z|-cutoff for outlier_count. Default 3." },
    },
    required: ["points"],
    additionalProperties: false,
  },
  async execute({ points, value_field, outlier_threshold }) {
    const cleaned = [];
    const times = [];
    for (const p of Array.isArray(points) ? points : []) {
      const v = extractValue(p, value_field);
      if (v == null) continue;
      cleaned.push(v);
      times.push(p?.t ? new Date(p.t).getTime() : null);
    }
    if (cleaned.length < 2) {
      return { ok: false, error: { code: "INSUFFICIENT_DATA", message: `Only ${cleaned.length} valid points.` } };
    }
    const mu = mean(cleaned);
    const sd = standardDeviation(cleaned);
    const cutoff = Number.isFinite(outlier_threshold) && outlier_threshold > 0 ? outlier_threshold : 3;
    let outliers = 0;
    if (sd > 0) {
      for (const v of cleaned) if (Math.abs(zScore(v, mu, sd)) > cutoff) outliers += 1;
    }
    // OLS trend on epoch-seconds since first point.
    let slope = null;
    let intercept = null;
    let r2 = null;
    if (times.every((t) => Number.isFinite(t))) {
      const t0 = times[0];
      const pairs = times.map((t, i) => [(t - t0) / 1000, cleaned[i]]);
      const { m, b } = linearRegression(pairs);
      slope = m;
      intercept = b;
      r2 = rSquared(pairs, linearRegressionLine({ m, b }));
    }
    return {
      ok: true,
      data: {
        count: cleaned.length,
        min: statsMin(cleaned),
        max: statsMax(cleaned),
        mean: mu,
        median: median(cleaned),
        stddev: sd,
        variance: variance(cleaned),
        trend: { slope, intercept, r2 },
        outlier_threshold: cutoff,
        outlier_count: outliers,
        quantiles: {
          p05: quantile(cleaned, 0.05),
          p25: quantile(cleaned, 0.25),
          p50: quantile(cleaned, 0.50),
          p75: quantile(cleaned, 0.75),
          p95: quantile(cleaned, 0.95),
        },
      },
    };
  },
};

// ── exported list (consumed by tools/index.js) ────────────────────

const analyticsTools = [
  // Tag discovery
  search_tags,
  list_tags_by_prefix,
  get_tag_metadata,
  get_tag_value,
  // Historian queries
  query_historian,
  query_historian_last,
  query_historian_raw,
  value_at,
  // Server-side analytics
  analyze_timeseries,
  find_anomalies,
  describe_distribution,
  detect_change_points,
  rate_of_change,
  // Multi-tag / pure-JS analytics
  correlate_tags,
  cross_correlate_lag,
  compare_ranges,
  compute_uptime,
  find_active_tags,
  // Local analysis over points already in scope
  analyze_points,
];

export default analyticsTools;
